import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Info,
  X,
  Sparkles,
  ArrowRight,
  Sliders,
} from "lucide-react";
import { t } from "@/i18n";
import {
  CONTINUITY_RISK_TYPES,
  isObservationFixable,
} from "@/domain/continuity";

/**
 * InformativeContinuityModal
 * Modal independiente para desplegar con amplitud y legibilidad la
 * "Información de reparto y notas consultivas" fuera del área recortada del Director.
 *
 * Requisitos:
 * - Título visible y botón X para cerrar.
 * - Scroll vertical propio y altura adaptada a la ventana (en móvil ocupa el espacio disponible).
 * - Escape y botón de cerrar devuelven al Director conservando la conversación intacta.
 * - Clasificación neutral ("Alcance del análisis") y prompt sin textos masivos en rojo.
 */
export const InformativeContinuityModal = ({
  open,
  onClose,
  observations = [],
  data = {},
  onNavigateToScene,
  onRequestFix,
  container,
}) => {
  const [expandedPrompts, setExpandedPrompts] = useState({});

  // Cierre accesible por tecla Escape
  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose?.();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose?.()}>
      <DialogContent
        data-testid="informative-continuity-modal"
        container={container}
        hideCloseButton={true}
        className="w-[95vw] sm:max-w-2xl max-h-[90vh] sm:max-h-[85vh] flex flex-col p-0 border-[#303030] bg-[#141414] text-[#f4f1ec] overflow-hidden rounded-xl shadow-2xl"
      >
        {/* Cabecera con título visible y X para cerrar */}
        <DialogHeader className="flex flex-row items-center justify-between px-5 py-4 border-b border-[#242424] bg-[#181818] shrink-0 text-left">
          <div className="flex items-center gap-2.5 min-w-0 pr-3">
            <div className="h-8 w-8 rounded-lg bg-[#222] border border-[#333] flex items-center justify-center text-[#f5b544] shrink-0">
              <Info size={16} />
            </div>
            <div className="min-w-0">
              <DialogTitle className="text-sm font-semibold text-[#f4f1ec] truncate">
                {t.continuity.informativeTitle}
              </DialogTitle>
              <DialogDescription className="text-[11px] text-[#888] mt-0.5 truncate">
                {observations.length} {observations.length === 1 ? "nota consultiva" : "notas consultivas"} · Supervisión sin contradicciones activas
              </DialogDescription>
            </div>
          </div>
          <button
            type="button"
            data-testid="informative-continuity-modal-close"
            onClick={onClose}
            className="rounded-lg p-1.5 text-[#888] hover:text-[#f4f1ec] hover:bg-[#262626] transition-colors shrink-0"
            title="Cerrar (Escape)"
            aria-label="Cerrar"
          >
            <X size={18} />
          </button>
        </DialogHeader>

        {/* Contenido legible con scroll vertical propio y altura adaptada a la ventana */}
        <div
          data-testid="informative-continuity-modal-body"
          className="flex-1 min-h-0 overflow-y-auto p-5 flex flex-col gap-3 text-xs"
        >
          {observations.map((obs) => {
            const isConflict = obs.severity === "conflict";
            const isWarning = obs.severity === "warning";
            const isMissing = obs.risk_type === CONTINUITY_RISK_TYPES.MISSING_INFORMATION;
            const isSingleScene = obs.id?.includes("single_scene_scope");

            const severityBadge = isSingleScene
              ? {
                  label: t.continuity?.analysisScope || "Alcance del análisis",
                  class: "border-zinc-500/40 bg-zinc-500/10 text-zinc-300",
                }
              : isConflict
              ? {
                  label: t.continuity.categories.real_contradiction,
                  class: "border-red-500/40 bg-red-500/10 text-red-400",
                }
              : isWarning
              ? {
                  label: t.continuity.categories.possible_risk,
                  class: "border-amber-500/40 bg-amber-500/10 text-amber-400",
                }
              : isMissing
              ? {
                  label: t.continuity.categories.missing_information,
                  class: "border-sky-500/40 bg-sky-500/10 text-sky-300",
                }
              : {
                  label: t.continuity.categories.registered_unused_asset,
                  class: "border-zinc-500/40 bg-zinc-500/10 text-zinc-400",
                };

            const affectedSceneIds = Array.isArray(obs.affected_scene_ids)
              ? obs.affected_scene_ids
              : Array.isArray(obs.affectedSceneIds)
              ? obs.affectedSceneIds
              : [];

            return (
              <div
                key={obs.id}
                data-testid={`assistant-continuity-alert-${obs.id}`}
                className={`rounded-lg border p-3.5 text-xs flex flex-col gap-2.5 ${
                  isConflict
                    ? "border-red-500/30 bg-[#1c1213]"
                    : isWarning
                    ? "border-amber-500/30 bg-[#1b1710]"
                    : "border-[#2b2b2b] bg-[#161616]"
                }`}
              >
                {/* Cabecera de la alerta */}
                <div className="flex flex-wrap items-center gap-2">
                  <span
                    className={`rounded border px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider ${severityBadge.class}`}
                  >
                    {severityBadge.label}
                  </span>
                  <span className="font-semibold text-[#f4f1ec]">{obs.title}</span>
                </div>

                {/* Explicación / Descripción */}
                <p className="text-[11px] leading-relaxed text-[#ccc]">{obs.description}</p>

                {/* Evidencia textual y datos faltantes */}
                {obs.evidence && Object.keys(obs.evidence).length > 0 && (
                  <div
                    data-testid={`assistant-continuity-evidence-${obs.id}`}
                    className="rounded bg-[#0c0c0c] border border-[#222] p-2.5 text-[10px] font-mono text-[#aaa] space-y-1"
                  >
                    {obs.evidence.canon_source && (
                      <div>
                        <span className="text-[#777]">Fuente:</span> {obs.evidence.canon_source}
                      </div>
                    )}
                    {obs.evidence.canon_text && (
                      <div>
                        <span className="text-[#777]">Canon:</span>{" "}
                        <span className="text-[#34d399] font-medium">"{obs.evidence.canon_text}"</span>
                      </div>
                    )}
                    {obs.evidence.scene_text && (
                      <div>
                        <span className="text-[#777]">Texto toma:</span>{" "}
                        {isSingleScene ? (
                          <div className="mt-1 font-sans">
                            <span className="text-[#ccc]">
                              {t.continuity?.singleSceneReviewedBrief ||
                                "Texto revisado sin inconsistencias internas."}
                            </span>{" "}
                            <button
                              type="button"
                              data-testid={`assistant-continuity-toggle-prompt-${obs.id}`}
                              onClick={() =>
                                setExpandedPrompts((prev) => ({
                                  ...prev,
                                  [obs.id]: !prev[obs.id],
                                }))
                              }
                              className="text-[#f5b544] hover:underline cursor-pointer text-[10px] inline-block ml-1 font-medium"
                            >
                              {expandedPrompts[obs.id]
                                ? t.continuity?.hideFullPrompt || "Ocultar texto"
                                : t.continuity?.viewFullPrompt || "Ver texto completo"}
                            </button>
                            {expandedPrompts[obs.id] && (
                              <div className="mt-1.5 max-h-36 overflow-y-auto rounded bg-[#161616] p-2 text-[10px] font-mono text-[#bbb] whitespace-pre-wrap border border-[#2a2a2a] leading-relaxed">
                                "{obs.evidence.scene_text}"
                              </div>
                            )}
                          </div>
                        ) : (
                          <span className={isConflict ? "text-[#f87171] font-medium" : "text-[#ccc]"}>
                            "{obs.evidence.scene_text}"
                          </span>
                        )}
                      </div>
                    )}
                    {obs.evidence.prev_scene_text && (
                      <div>
                        <span className="text-[#777]">
                          Toma anterior (#{obs.evidence.prev_scene_order}):
                        </span>{" "}
                        "{obs.evidence.prev_scene_text}"
                      </div>
                    )}
                    {obs.evidence.target_scene_text && !obs.evidence.scene_text && (
                      <div>
                        <span className="text-[#777]">
                          Toma actual (#{obs.evidence.target_scene_order}):
                        </span>{" "}
                        <span className={isConflict ? "text-[#f87171] font-medium" : "text-[#ccc]"}>
                          "{obs.evidence.target_scene_text}"
                        </span>
                      </div>
                    )}
                  </div>
                )}

                {/* Avisos específicos de cámara */}
                {obs.id?.includes("missing_camera") && (
                  <p className="text-[10px] text-sky-400/90 italic">
                    {t.assistant.missingCameraNotice}
                  </p>
                )}
                {obs.id?.includes("unstructured_camera") && (
                  <p className="text-[10px] text-sky-400/90 italic">
                    {t.assistant.unstructuredCameraNotice ||
                      "Sin parámetros estructurados de cámara; el prompt contiene indicaciones."}
                  </p>
                )}

                {/* Recomendación consultiva */}
                <div className="rounded border border-[#f5b544]/20 bg-[#1c1810] p-2.5 text-[11px] text-[#e0cfb3] flex items-start gap-1.5">
                  <Sparkles size={12} className="text-[#f5b544] shrink-0 mt-0.5" />
                  <div className="leading-relaxed">
                    <strong className="text-[#f5b544] font-medium">
                      {t.continuity.recommendation}:
                    </strong>{" "}
                    {obs.recommendation}
                  </div>
                </div>

                {/* Propuesta de corrección consultiva (sin auto-apply) */}
                {obs.proposed_fix && (
                  <div
                    data-testid={`assistant-continuity-fix-${obs.id}`}
                    className="rounded bg-[#121212] border border-[#2d2d2d] p-2.5 text-[11px] flex flex-col gap-1 text-[#aaa]"
                  >
                    <span className="text-[10px] text-[#f5b544] font-medium">
                      {t.continuity.proposedChange} ({t.assistant.consultiveNotice}):
                    </span>
                    <div className="font-mono text-[10px] text-[#888] line-through whitespace-pre-wrap">
                      {obs.proposed_fix.before_text}
                    </div>
                    <div className="font-mono text-[10px] text-[#a7f3d0] whitespace-pre-wrap">
                      {obs.proposed_fix.after_text}
                    </div>
                    {obs.proposed_fix.explanation && (
                      <div className="text-[10px] text-[#888] italic">
                        {obs.proposed_fix.explanation}
                      </div>
                    )}
                  </div>
                )}

                {/* Navegación a las escenas afectadas */}
                {affectedSceneIds.length > 0 && onNavigateToScene && (
                  <div className="flex flex-wrap items-center gap-1.5 pt-1 border-t border-[#262626]">
                    <span className="text-[10px] text-[#888]">{t.continuity.affectedScenes}:</span>
                    {affectedSceneIds.map((scId) => {
                      const sc = (data.scenes || []).find((s) => s.id === scId);
                      const scOrder = sc ? (sc.order ?? 0) + 1 : "?";
                      const scTitle = sc ? sc.title || `Toma #${scOrder}` : scId;
                      return (
                        <button
                          key={scId}
                          type="button"
                          data-testid={`assistant-continuity-scene-link-${obs.id}-${scId}`}
                          onClick={() => {
                            onClose?.();
                            onNavigateToScene(scId, obs);
                          }}
                          className="inline-flex items-center gap-1 rounded bg-[#222] border border-[#333] px-1.5 py-0.5 text-[10px] font-medium text-[#ccc] hover:border-[#f5b544]/60 hover:text-[#f4f1ec] transition-colors cursor-pointer"
                          title={`${t.assistant.openInStoryboard || t.assistant.openScene}: ${scTitle}`}
                        >
                          <ArrowRight size={10} className="text-[#f5b544]" />
                          <span>
                            Toma #{scOrder}: {scTitle}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}

                {/* Solicitar corrección en chat (únicamente si el raccord es corregible modificando el prompt) */}
                {isObservationFixable(obs) && onRequestFix && (
                  <div className="pt-1.5 flex items-center justify-between gap-2 border-t border-[#262626]">
                    <button
                      type="button"
                      data-testid={`assistant-continuity-request-fix-${obs.id}`}
                      onClick={() => {
                        onClose?.();
                        onRequestFix(obs);
                      }}
                      className="inline-flex items-center gap-1 rounded bg-[#201c13] border border-[#f5b544]/50 px-2 py-1 text-[11px] font-medium text-[#f5b544] hover:bg-[#2a2215] transition-colors cursor-pointer"
                      title={t.assistant?.requestFix || "Solicitar corrección"}
                    >
                      <Sparkles size={11} className="text-[#f5b544]" />
                      <span>{t.assistant?.requestFix || "Solicitar corrección"}</span>
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Pie del modal */}
        <div className="flex items-center justify-between px-5 py-3 border-t border-[#242424] bg-[#181818] shrink-0 text-xs">
          <span className="text-[11px] text-[#777] truncate pr-2">
            {t.continuity.disclaimer}
          </span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              data-testid="informative-continuity-modal-bottom-close"
              onClick={onClose}
              className="rounded border border-[#333] bg-[#222] px-3 py-1 text-xs font-medium text-[#ccc] hover:text-white hover:border-[#444] transition-colors shrink-0"
            >
              {t.common?.close || "Cerrar"}
            </button>
            <button
              type="button"
              data-testid="assistant-continuity-informative-fold-bottom"
              onClick={onClose}
              className="sr-only"
              aria-label="Cerrar modal informativo"
            >
              {t.common?.close || "Cerrar"}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default InformativeContinuityModal;
